import { PriceStatus } from '@toggle/helpers';
import {
  AssetSubClassNames,
  Entity,
  mapAssetClass,
  MappedEntity,
  PrimaryMethod,
  SnakeMeta,
} from '@toggle/toggle';
import { StoreApi } from 'zustand';

import { PriceUpdate, TickerPayload } from '~/services/price-ws/types';
import { priceDecimals } from '~/utils/currency/currency';
import {
  getPriceChange,
  getTickerForLivePrice,
} from '~/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';

export enum Identifier {
  Global = 'Global',
  Watchlist = 'Watchlist',
}

export interface LivePriceChange {
  status: PriceStatus;
  change: string;
  proportionChange: string;
}

export interface LivePriceInfo {
  price: number;
  priceChange: LivePriceChange;
  decimals: number;
  time: string;
  lastKnownPrice: number;
  isLive: boolean;
  priceBefore: number;
}

export interface LivePriceData {
  [key: string]: LivePriceInfo;
}

export type SubscribeConfig = {
  priceEntities: PriceEntity[];
  identifier?: Identifier;
  delayedPrices: boolean;
};

export type SubscribeFn = (config: SubscribeConfig) => () => void;

export interface LivePriceState {
  livePriceData: LivePriceData;
  componentsWatchingData: { [key: string]: string[] };
  subscribe: SubscribeFn;
}

export interface PriceEntity {
  entity: {
    ticker: string;
    sub_class: MappedEntity['sub_class'];
    primary_method: PrimaryMethod | '';
    subscribable_ticker?: string;
    trading_country: Entity['trading_country'];
  };
  lastKnownPrice: number;
  priceBefore: number;
  time?: string;
}

export const mapLivePriceData = ({
  entity,
  lastKnownPrice,
  priceBefore,
  time = '',
}: PriceEntity) => {
  const decimals = priceDecimals(entity.sub_class as AssetSubClassNames)(
    lastKnownPrice
  );

  return {
    price: lastKnownPrice,
    decimals,
    priceChange: getPriceChange({
      lastPrice: priceBefore,
      newPrice: lastKnownPrice,
      decimals,
      isPrice: entity.primary_method === PrimaryMethod.Price,
    }).priceChange,
    lastKnownPrice,
    time,
    isLive: false,
    priceBefore,
  };
};

export const mapPriceEntityToLivePriceData = (
  priceEntities: PriceEntity[],
  livePriceData: LivePriceData
) => {
  return (
    priceEntities
      // Some charts dont have a snake meta, but try to use the WS resulting in saving invalid data in livePriceData
      // Filter out entities without snake meta to avoid
      .filter(entity => entity.lastKnownPrice !== 0)
      .reduce(
        ({ data, tickers }, priceEntity) => {
          const entity = priceEntity.entity;
          const tradingCountry = entity.trading_country;
          const tickerForLivePrice = getTickerForLivePrice(entity);

          const existingTicker = tickerForLivePrice
            ? data[tickerForLivePrice]
            : undefined;

          if (tickerForLivePrice && !existingTicker) {
            const updatedTickers = [
              ...tickers,
              {
                ticker: tickerForLivePrice,
                trading_country: tradingCountry?.long ?? '',
              },
            ];

            return {
              data: {
                ...data,
                [tickerForLivePrice]: mapLivePriceData(priceEntity),
              },
              tickers: updatedTickers,
            };
          }

          const shouldUpdateBaseline =
            existingTicker?.lastKnownPrice !== undefined &&
            existingTicker.lastKnownPrice !== priceEntity.lastKnownPrice;

          if (tickerForLivePrice && shouldUpdateBaseline) {
            const updatedTickers = [
              ...tickers,
              {
                ticker: tickerForLivePrice,
                trading_country: tradingCountry?.long ?? '',
              },
            ];

            return {
              data: {
                ...data,
                [tickerForLivePrice]: {
                  ...existingTicker,
                  ...mapLivePriceData(priceEntity),
                  isLive: existingTicker.isLive,
                },
              },
              tickers: updatedTickers,
            };
          }

          return { data, tickers };
        },
        { data: livePriceData, tickers: [] as TickerPayload[] }
      )
  );
};

export const mapNewLivePriceDataToWatchedData = (
  priceEntities: PriceEntity[],
  newLivePriceData: LivePriceData,
  identifier: Identifier,
  componentsWatchingData: { [key: string]: string[] }
) => {
  const allEntityTickers = priceEntities
    .map(entity => entity.entity.subscribable_ticker)
    .filter(ticker => !!ticker);
  return Object.keys(newLivePriceData).reduce((acc: any, key) => {
    if (!allEntityTickers.includes(key)) {
      return acc;
    }

    if (acc?.[key] && !acc[key].includes(identifier)) {
      acc[key].push(identifier);
    } else {
      acc[key] = [identifier];
    }

    return acc;
  }, componentsWatchingData);
};

export const priceUpdater =
  (set: StoreApi<LivePriceState>['setState']) =>
  ({ price, time, ticker }: PriceUpdate) => {
    set(state => {
      const subscribableTicker = ticker;
      const tickerData = state.livePriceData[subscribableTicker];

      if (!tickerData) {
        return state;
      }

      const lastKnownPrice = tickerData.lastKnownPrice;

      const { priceChange } = getPriceChange({
        lastPrice: lastKnownPrice,
        newPrice: price,
        decimals: tickerData.decimals,
      });

      return {
        ...state,
        livePriceData: {
          ...state.livePriceData,
          [subscribableTicker]: {
            price,
            priceChange,
            lastKnownPrice,
            isLive: true,
            time,
            priceBefore: tickerData.priceBefore,
            decimals: tickerData.decimals,
          },
        },
      };
    });
  };

export const updateWatching = (
  set: StoreApi<LivePriceState>['setState'],
  get: StoreApi<LivePriceState>['getState'],
  identifier: Identifier,
  subscribableTicker?: string
) => {
  if (!subscribableTicker) {
    return false;
  }

  if (get().componentsWatchingData[subscribableTicker]?.length > 1) {
    const newWatching = get().componentsWatchingData[subscribableTicker];

    set({
      componentsWatchingData: {
        ...get().componentsWatchingData,
        [subscribableTicker]: newWatching.filter(
          (id: string) => id !== identifier
        ),
      },
    });
    return false;
  } else {
    const { [subscribableTicker]: _, ...rest } = get().componentsWatchingData;
    set({
      componentsWatchingData: rest,
    });
    return true;
  }
};

export const mapEntityToPriceEntity = (
  entity?: Entity,
  snakeMeta?: SnakeMeta
): PriceEntity[] =>
  entity && snakeMeta?.last_value
    ? [
        {
          entity: {
            ticker: entity.ticker,
            sub_class: mapAssetClass(entity).sub_class,
            primary_method: entity.primary_method,
            subscribable_ticker: entity.subscribable_ticker,
            trading_country: entity.trading_country,
          },
          lastKnownPrice: snakeMeta.last_value,
          priceBefore: snakeMeta.before_last_value,
          time: new Date(snakeMeta.last_timestamp / 100_0000).toISOString(),
        },
      ]
    : [];
